/**
 * The signed-in hint: a small, non-secret note that the Music24 web app (music24.com/app) keeps in
 * `localStorage` while a person is signed in. music24.com and the web app share one origin, so
 * music24.com reads the note and greets the person ("Hi Bartosz", "Open Music24").
 *
 * The note holds NO token and no id: only the first name (or none) and the time the web app last
 * wrote it. The website never reads the session token and never calls the API.
 *
 * Writers: the web app writes the note when a session starts and when it loads the user
 * (`GET /users/:id`), and removes it on sign-out and when the session ends (a refused refresh).
 * Reader: music24.com, after the page loads (the server HTML is always the signed-out HTML).
 *
 * The website cannot import fym-shared at build time, so it keeps a copy of this file
 * (`music24-website/app/src/components/site/signedIn/signedInHint.ts`). A test there pins the copy
 * to this file. Change both together.
 */

export const MUSIC24_SIGNED_IN_HINT_KEY = 'm24SignedInHint';
export const MUSIC24_SIGNED_IN_HINT_VERSION = 1;
/**
 * A note older than this reads as "signed out". The session refresh accepts a token for 30 days
 * after it expired, so a person who did not open the web app for longer must sign in again.
 */
export const MUSIC24_SIGNED_IN_HINT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** The longest first name that the note keeps. A longer name is cut. */
export const MUSIC24_SIGNED_IN_HINT_NAME_MAX = 40;
/** A clock that runs a little ahead in another tab still gives a valid note. */
const FUTURE_TOLERANCE_MS = 24 * 60 * 60 * 1000;

export interface Music24SignedInHint {
  v: typeof MUSIC24_SIGNED_IN_HINT_VERSION;
  /** The first name, or null when the web app knows none (an email sign-in). */
  firstName: string | null;
  /** When the web app wrote the note (ms since the epoch). */
  at: number;
}

/** The part of the browser `Storage` that the helpers use (fym-shared has no DOM types). */
export interface SignedInHintStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// Control characters and the bidi controls: a name must not change the text around it.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\u0000-\u001f\u007f-\u009f‎‏‪-‮⁦-⁩]/g;

/**
 * The first name of a full name: the first word, without control characters, at most
 * `MUSIC24_SIGNED_IN_HINT_NAME_MAX` characters. Null for an empty value and for a value with `@`
 * (an email address is not a name).
 */
export function firstNameOf(fullName: string | null | undefined): string | null {
  if (typeof fullName !== 'string') {
    return null;
  }
  const first = fullName.replace(UNSAFE_CHARACTERS, ' ').trim().split(/\s+/)[0] ?? '';
  if (first === '' || first.includes('@')) {
    return null;
  }
  return Array.from(first).slice(0, MUSIC24_SIGNED_IN_HINT_NAME_MAX).join('');
}

export function buildSignedInHint(firstName: string | null | undefined, now: number): Music24SignedInHint {
  return { v: MUSIC24_SIGNED_IN_HINT_VERSION, firstName: firstNameOf(firstName), at: now };
}

/**
 * Reads a stored value. Null for no value, a value that is not JSON, another version, a bad
 * shape, a note older than `MUSIC24_SIGNED_IN_HINT_MAX_AGE_MS` or a note from the future.
 * A bad first name gives a note with no name, not "signed out".
 */
export function parseSignedInHint(raw: string | null | undefined, now: number): Music24SignedInHint | null {
  if (typeof raw !== 'string' || raw === '') {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.v !== MUSIC24_SIGNED_IN_HINT_VERSION) {
    return null;
  }
  const { at } = record;
  if (typeof at !== 'number' || !Number.isFinite(at)) {
    return null;
  }
  if (now - at > MUSIC24_SIGNED_IN_HINT_MAX_AGE_MS || at - now > FUTURE_TOLERANCE_MS) {
    return null;
  }
  const firstName = typeof record.firstName === 'string' ? firstNameOf(record.firstName) : null;
  return { v: MUSIC24_SIGNED_IN_HINT_VERSION, firstName, at };
}

export function serializeSignedInHint(hint: Music24SignedInHint): string {
  return JSON.stringify(hint);
}

/** The note in `storage`, or null. Never throws (a blocked storage reads as "signed out"). */
export function readSignedInHint(
  storage: SignedInHintStorage | null | undefined,
  now: number
): Music24SignedInHint | null {
  if (!storage) {
    return null;
  }
  try {
    return parseSignedInHint(storage.getItem(MUSIC24_SIGNED_IN_HINT_KEY), now);
  } catch {
    return null;
  }
}

/** Stores the note. Never throws: a full or blocked storage only loses the greeting. */
export function writeSignedInHint(
  storage: SignedInHintStorage | null | undefined,
  firstName: string | null | undefined,
  now: number
): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(MUSIC24_SIGNED_IN_HINT_KEY, serializeSignedInHint(buildSignedInHint(firstName, now)));
  } catch {
    /* A blocked or full storage: the website shows the signed-out page. */
  }
}

/** Removes the note. Never throws. */
export function clearSignedInHint(storage: SignedInHintStorage | null | undefined): void {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(MUSIC24_SIGNED_IN_HINT_KEY);
  } catch {
    /* A blocked storage holds no note. */
  }
}
